import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Table, Badge, Modal, Form, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { calculateSeminarScore } from '../utils/seminarUtils';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaEye, FaEdit, FaCalendarAlt, FaFilePdf, FaDownload, FaEnvelope, FaClipboardList, FaFileAlt } from 'react-icons/fa';
import SeminarDetail from '../components/seminar/SeminarDetail';
import RekapNilaiAkhir from '../components/seminar/RekapNilaiAkhir';
import { useLocation } from 'react-router-dom';
import { generateUndanganPDF, generateAbsensiPDF } from '../utils/pdfGenerator';

const Seminar = () => {
  const { currentUser, userRole } = useAuth();
  const location = useLocation();
  // Check if we're in the mahasiswa-uji path
  const isMahasiswaUji = location.pathname === '/mahasiswa-uji';
  const [tugasAkhir, setTugasAkhir] = useState(null);
  const [seminarList, setSeminarList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showNilaiModal, setShowNilaiModal] = useState(false);
  const [selectedSeminar, setSelectedSeminar] = useState(null);
  const [nilaiForm, setNilaiForm] = useState({});
  const [catatanMasukan, setCatatanMasukan] = useState([]);
  const [activeTab, setActiveTab] = useState('proposal');
  const [showUndanganModal, setShowUndanganModal] = useState(false);
  const [tugasAkhirWithPembimbing, setTugasAkhirWithPembimbing] = useState(null);
  const [showRekapModal, setShowRekapModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    if (currentUser && userRole) {
      fetchData();
    }
  }, [currentUser, userRole]);

  // Fetch pembimbing data saat modal undangan dibuka
  useEffect(() => {
    const fetchPembimbingForUndangan = async () => {
      if (showUndanganModal && selectedSeminar && selectedSeminar.tugasAkhirId) {
        try {
          const taDocRef = doc(db, "tugasAkhir", selectedSeminar.tugasAkhirId);
          const taDoc = await getDoc(taDocRef);

          if (taDoc.exists()) {
            const taData = taDoc.data();

            // Fetch pembimbing data if pembimbingId exists
            if (taData.pembimbingId && Array.isArray(taData.pembimbingId)) {
              const pembimbingList = [];
              for (const pembimbingId of taData.pembimbingId) {
                try {
                  const pembimbingDoc = await getDoc(doc(db, "users", pembimbingId));
                  if (pembimbingDoc.exists()) {
                    pembimbingList.push({
                      id: pembimbingDoc.id,
                      ...pembimbingDoc.data()
                    });
                  }
                } catch (error) {
                  console.error(`Error fetching pembimbing ${pembimbingId}:`, error);
                }
              }
              taData.pembimbing = pembimbingList;
            }

            setTugasAkhirWithPembimbing(taData);
          }
        } catch (error) {
          console.error("Error fetching tugas akhir for undangan:", error);
        }
      }
    };

    if (showUndanganModal) {
      fetchPembimbingForUndangan();
    } else {
      setTugasAkhirWithPembimbing(null);
    }
  }, [showUndanganModal, selectedSeminar]);

  const fetchData = async () => {
    try {
      setLoading(true);

      if (!currentUser || !userRole) {
        setLoading(false);
        return;
      }

      // Ambil data berdasarkan peran
      if (userRole === 'mahasiswa') {
        // Mahasiswa: ambil tugas akhir berdasarkan mahasiswaId
        const taQuery = query(
          collection(db, "tugasAkhir"),
          where("mahasiswaId", "==", currentUser.uid)
        );
        const taSnapshot = await getDocs(taQuery);

        if (!taSnapshot.empty) {
          const taData = taSnapshot.docs[0].data();
          taData.id = taSnapshot.docs[0].id;

          setTugasAkhir(taData);

          // Ambil data seminar
          if (taData.status === 'approved') {
            const seminarQuery = query(
              collection(db, "seminar"),
              where("tugasAkhirId", "==", taData.id),
              orderBy("tanggal", "desc")
            );
            const seminarSnapshot = await getDocs(seminarQuery);

            const seminarData = [];
            seminarSnapshot.forEach(doc => {
              seminarData.push({
                id: doc.id,
                ...doc.data()
              });
            });

            setSeminarList(seminarData);
          }
        }
      } else if (userRole === 'dosen') {
        // Dosen: ambil seminar berdasarkan pengujiId atau pembimbingId
        const seminarQuery = query(
          collection(db, "seminar"),
          where("pengujiId", "array-contains", currentUser.uid)
        );
        const seminarSnapshot = await getDocs(seminarQuery);

        const seminarData = [];
        for (const docSnapshot of seminarSnapshot.docs) {
          const seminarItem = {
            id: docSnapshot.id,
            ...docSnapshot.data()
          };

          // Ambil data tugas akhir
          if (seminarItem.tugasAkhirId) {
            try {
              const taDocRef = doc(db, "tugasAkhir", seminarItem.tugasAkhirId);
              const taDoc = await getDoc(taDocRef);
              if (taDoc.exists()) {
                seminarItem.tugasAkhir = taDoc.data();

                // Ambil data mahasiswa
                if (seminarItem.tugasAkhir.mahasiswaId) {
                  const mahasiswaDocRef = doc(db, "users", seminarItem.tugasAkhir.mahasiswaId);
                  const mahasiswaDoc = await getDoc(mahasiswaDocRef);
                  if (mahasiswaDoc.exists()) {
                    seminarItem.mahasiswa = mahasiswaDoc.data();
                  }
                }
              }
            } catch (fetchError) {
              console.error("Error fetching TA data:", fetchError);
            }
          }

          seminarData.push(seminarItem);
        }

        setSeminarList(seminarData);
      } else if (userRole === 'pj') {
        // PJ: ambil semua seminar
        const seminarQuery = query(
          collection(db, "seminar"),
          orderBy("tanggal", "desc")
        );
        const seminarSnapshot = await getDocs(seminarQuery);

        const seminarData = [];
        for (const docSnapshot of seminarSnapshot.docs) {
          const seminarItem = {
            id: docSnapshot.id,
            ...docSnapshot.data()
          };

          // Ambil data tugas akhir
          if (seminarItem.tugasAkhirId) {
            try {
              const taDocRef = doc(db, "tugasAkhir", seminarItem.tugasAkhirId);
              const taDoc = await getDoc(taDocRef);
              if (taDoc.exists()) {
                seminarItem.tugasAkhir = taDoc.data();

                // Ambil data mahasiswa
                if (seminarItem.tugasAkhir.mahasiswaId) {
                  const mahasiswaDocRef = doc(db, "users", seminarItem.tugasAkhir.mahasiswaId);
                  const mahasiswaDoc = await getDoc(mahasiswaDocRef);
                  if (mahasiswaDoc.exists()) {
                    seminarItem.mahasiswa = mahasiswaDoc.data();
                  }
                }
              }
            } catch (fetchError) {
              console.error("Error fetching TA data:", fetchError);
            }
          }

          seminarData.push(seminarItem);
        }

        setSeminarList(seminarData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Terjadi kesalahan saat mengambil data");
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetail = (seminar) => {
    setSelectedSeminar(seminar);
    setShowDetailModal(true);
  };

  const handleShowNilai = (seminar) => {
    setSelectedSeminar(seminar);

    // Inisialisasi form nilai berdasarkan kriteria penilaian
    const initialNilai = {};
    if (seminar.kriteriaPenilaian) {
      seminar.kriteriaPenilaian.forEach(kriteria => {
        // Cek apakah sudah ada nilai dari penguji ini
        if (seminar.nilai && seminar.nilai[currentUser.uid]) {
          initialNilai[kriteria.id] = seminar.nilai[currentUser.uid][kriteria.id] || 0;
        } else {
          initialNilai[kriteria.id] = 0;
        }
      });
    }

    // Inisialisasi catatan masukan jika sudah ada
    const existingCatatan = seminar.nilai && seminar.nilai[currentUser.uid] && seminar.nilai[currentUser.uid].catatanMasukan
      ? Array.isArray(seminar.nilai[currentUser.uid].catatanMasukan)
        ? seminar.nilai[currentUser.uid].catatanMasukan
        : []
      : [];

    setNilaiForm(initialNilai);
    setCatatanMasukan(existingCatatan);
    setShowNilaiModal(true);
  };

  const handleCatatanChange = (idx, value) => {
    const updated = [...catatanMasukan];
    updated[idx] = value;
    setCatatanMasukan(updated);
  };

  const handleAddCatatan = () => {
    setCatatanMasukan([...catatanMasukan, '']);
  };

  const handleRemoveCatatan = (idx) => {
    setCatatanMasukan(catatanMasukan.filter((_, i) => i !== idx));
  };

  const handleNilaiChange = (kriteriaId, nilai) => {
    setNilaiForm({
      ...nilaiForm,
      [kriteriaId]: parseFloat(nilai)
    });
  };

  // Use the shared utility function for consistent score calculation
  const calculateFinalScore = (seminar) => {
    if (!seminar || !seminar.nilai || typeof seminar.nilai !== 'object') return 0;

    // Use the shared score calculation utility
    const result = calculateSeminarScore(seminar);

    // Return the calculated final score
    return result.finalScore;
  };

  // Check if the current user is Pembimbing 1 for this seminar
  const isPembimbing1 = (userId, seminar) => {
    if (!userId || !seminar || !seminar.tugasAkhir) return false;

    // Check if the user is the first in the pembimbingId array
    if (seminar.tugasAkhir.pembimbingId &&
      Array.isArray(seminar.tugasAkhir.pembimbingId) &&
      seminar.tugasAkhir.pembimbingId.length > 0) {
      return userId === seminar.tugasAkhir.pembimbingId[0];
    }

    return false;
  };

  const handleSubmitNilai = async () => {
    try {
      if (!selectedSeminar) return;

      setLoading(true);

      // Update nilai di Firestore
      const seminarRef = doc(db, "seminar", selectedSeminar.id);
      const seminarDoc = await getDoc(seminarRef);

      if (seminarDoc.exists()) {
        const seminarData = seminarDoc.data();

        // Inisialisasi objek nilai jika belum ada
        const nilai = seminarData.nilai || {};

        // Tambahkan nilai dari penguji ini beserta catatan masukan
        nilai[currentUser.uid] = {
          ...nilaiForm,
          catatanMasukan: catatanMasukan.filter(c => c && c.trim() !== ''), // Filter empty catatan
          timestamp: new Date().toISOString(),
          pengujiId: currentUser.uid
        };

        // Check if all reviewers have submitted scores
        const allPengujiHaveScored = seminarData.pengujiId &&
          Array.isArray(seminarData.pengujiId) &&
          seminarData.pengujiId.length > 0 &&
          seminarData.pengujiId.every(id => id && typeof id === 'string' && nilai && nilai[id]);

        // Prepare update data
        const updateData = { nilai };

        // If all reviewers have submitted scores and status is not completed, mark as completed
        // If status is already completed, keep it as completed
        if (allPengujiHaveScored) {
          if (seminarData.status !== 'completed') {
            // Mark as completed for the first time
            updateData.status = 'completed';
            updateData.completedAt = new Date().toISOString();
          }
          // If already completed, status remains completed (no change needed)
        }

        // Update dokumen
        await updateDoc(seminarRef, updateData);

        if (allPengujiHaveScored && seminarData.status !== 'completed') {
          console.log("Seminar marked as completed");
        } else if (allPengujiHaveScored && seminarData.status === 'completed') {
          console.log("Nilai updated for completed seminar - status remains completed");
        }

        // Pesan sukses yang berbeda untuk edit atau input baru
        const isEditing = seminarData.nilai && seminarData.nilai[currentUser.uid];
        const isCompleted = seminarData.status === 'completed';

        if (isEditing && isCompleted) {
          setSuccess("Nilai berhasil diperbarui. Nilai akhir seminar telah diupdate.");
        } else if (isEditing) {
          setSuccess("Nilai berhasil diperbarui.");
        } else {
          setSuccess("Nilai berhasil disimpan.");
        }

        setShowNilaiModal(false);

        // Refresh data
        fetchData();
      }
    } catch (error) {
      console.error("Error submitting nilai:", error);
      setError("Terjadi kesalahan saat menyimpan nilai");
    } finally {
      setLoading(false);
    }
  };

  const getFilteredSeminar = (type) => {
    return seminarList.filter(seminar => seminar.jenis === type);
  };

  // Handle generate undangan PDF untuk pembimbing/penguji
  const handleGenerateUndangan = async (seminar, recipientId, role) => {
    try {
      setLoading(true);

      // Fetch recipient data
      const recipientDoc = await getDoc(doc(db, "users", recipientId));
      if (!recipientDoc.exists()) {
        setError("Data penerima undangan tidak ditemukan");
        return;
      }

      const recipientData = recipientDoc.data();

      // Generate PDF
      const pdfBlob = await generateUndanganPDF(
        seminar,
        tugasAkhir,
        {
          nama: recipientData.nama || 'Dosen',
          nip: recipientData.nip || '-'
        },
        role
      );

      // Download PDF
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      // Nama file hanya dengan nama penerima, tanpa role
      link.download = `Undangan_${recipientData.nama.replace(/\s+/g, '_')}_${seminar.mahasiswaNIM || 'seminar'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess(`PDF undangan untuk ${role} berhasil diunduh`);
    } catch (error) {
      console.error("Error generating undangan PDF:", error);
      setError("Terjadi kesalahan saat generate PDF undangan: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle download absensi PDF langsung dari public folder
  const handleGenerateAbsensi = async (seminar) => {
    try {
      setLoading(true);

      // Download file daftarhadir.pdf langsung dari public folder
      const response = await fetch('/daftarhadir.pdf');
      if (!response.ok) {
        throw new Error('File daftarhadir.pdf tidak ditemukan');
      }

      const blob = await response.blob();

      // Download PDF
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Daftar_Hadir_${seminar.jenis}_${seminar.mahasiswaNIM || 'seminar'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess("File absensi berhasil diunduh");
    } catch (error) {
      console.error("Error downloading absensi PDF:", error);
      setError("Terjadi kesalahan saat mengunduh file absensi: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderSeminarTable = (type) => {
    const filteredSeminar = getFilteredSeminar(type);

    if (filteredSeminar.length === 0) {
      return (
        <Alert variant="info">
          Belum ada jadwal {type === 'proposal' ? 'seminar proposal' : type === 'hasil' ? 'seminar hasil' : 'sidang komprehensif'}
        </Alert>
      );
    }

    return (
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>No</th>
            {(userRole === 'dosen' || userRole === 'pj') && <th>Mahasiswa</th>}
            <th>Tanggal</th>
            <th>Tempat</th>
            <th>Status</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {filteredSeminar.map((seminar, index) => (
            <tr key={seminar.id}>
              <td>{index + 1}</td>
              {(userRole === 'dosen' || userRole === 'pj') && (
                <td>
                  {seminar.mahasiswa?.nama || seminar.mahasiswaNama || '-'}
                  <br />
                  <small>{seminar.mahasiswa?.nim || seminar.mahasiswaNIM || '-'}</small>
                  {seminar.tugasAkhir?.judul && (
                    <div className="mt-1">
                      <small className="text-muted">{seminar.tugasAkhir.judul || seminar.judulTA || '-'}</small>
                    </div>
                  )}
                </td>
              )}
              <td>{new Date(seminar.tanggal).toLocaleDateString()}</td>
              <td>{seminar.tempat}</td>
              <td>
                <Badge bg={
                  seminar.status === 'scheduled' ? 'info' :
                    seminar.status === 'completed' ? 'success' :
                      seminar.status === 'cancelled' ? 'danger' : 'secondary'
                }>
                  {seminar.status === 'scheduled' ? 'Terjadwal' :
                    seminar.status === 'completed' ? 'Selesai' :
                      seminar.status === 'cancelled' ? 'Dibatalkan' : seminar.status}
                </Badge>
              </td>
              <td>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleShowDetail(seminar)}
                  >
                    <FaEye />
                  </Button>

                  {userRole === 'dosen' && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleShowNilai(seminar)}
                      title={seminar.status === 'completed' ? 'Edit Nilai (Seminar Selesai)' : 'Isi/Edit Nilai'}
                    >
                      <FaEdit />
                    </Button>
                  )}

                  {/* Tombol generate PDF untuk mahasiswa yang sudah terjadwal/selesai */}
                  {userRole === 'mahasiswa' && (seminar.status === 'scheduled' || seminar.status === 'completed') && (
                    <>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => {
                          setSelectedSeminar(seminar);
                          // Show modal untuk pilih pembimbing/penguji
                          setShowUndanganModal(true);
                        }}
                        title="Generate Undangan"
                      >
                        <FaEnvelope />
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleGenerateAbsensi(seminar)}
                        title="Generate Absensi"
                      >
                        <FaClipboardList />
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // Kelompokkan seminar selesai per tugas akhir untuk rekap nilai akhir.
  // Untuk dosen, hanya tampilkan mahasiswa di mana ia menjadi Pembimbing 1.
  const getGroupedByStudent = () => {
    const groups = {};

    seminarList.forEach((seminar) => {
      if (seminar.status !== 'completed') return;
      if (seminar.superseded === true) return;
      if (!seminar.tugasAkhirId) return;
      if (!['proposal', 'hasil', 'kompre'].includes(seminar.jenis)) return;

      // Batasi untuk dosen: hanya jika ia Pembimbing 1 pada tugas akhir ini
      if (userRole === 'dosen') {
        const pembimbing1Id = seminar.tugasAkhir?.pembimbingId?.[0];
        if (pembimbing1Id !== currentUser?.uid) return;
      }

      if (!groups[seminar.tugasAkhirId]) {
        groups[seminar.tugasAkhirId] = {
          tugasAkhirId: seminar.tugasAkhirId,
          mahasiswaNama: seminar.mahasiswa?.nama || seminar.mahasiswaNama,
          mahasiswaNIM: seminar.mahasiswa?.nim || seminar.mahasiswaNIM,
          judulTA: seminar.tugasAkhir?.judul || seminar.judulTA,
          proposal: null,
          hasil: null,
          kompre: null
        };
      }

      const existing = groups[seminar.tugasAkhirId][seminar.jenis];
      if (!existing || new Date(seminar.tanggal) >= new Date(existing.tanggal)) {
        groups[seminar.tugasAkhirId][seminar.jenis] = seminar;
      }
    });

    return Object.values(groups);
  };

  const handleShowRekap = (group) => {
    setSelectedGroup(group);
    setShowRekapModal(true);
  };

  const renderNilaiAkhirTable = () => {
    const groups = getGroupedByStudent();

    if (groups.length === 0) {
      return (
        <Alert variant="info">
          {userRole === 'dosen'
            ? 'Belum ada mahasiswa bimbingan (sebagai Pembimbing 1) dengan seminar/sidang yang selesai untuk direkap nilai akhirnya'
            : 'Belum ada seminar/sidang yang selesai untuk direkap nilai akhirnya'}
        </Alert>
      );
    }

    return (
      <>
        <Alert variant="secondary" className="py-2">
          Nilai akhir dihitung dari Seminar Proposal (30%), Seminar Hasil (40%),
          dan Sidang Komprehensif (30%). Berita acara ditandatangani seluruh
          pembimbing & penguji dan diketahui Koordinator Tugas Akhir.
        </Alert>
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>No</th>
              <th>Mahasiswa</th>
              <th>Judul TA</th>
              <th className="text-center">Proposal</th>
              <th className="text-center">Hasil</th>
              <th className="text-center">Kompre</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group, index) => (
              <tr key={group.tugasAkhirId}>
                <td>{index + 1}</td>
                <td>
                  {group.mahasiswaNama || '-'}<br />
                  <small>{group.mahasiswaNIM || '-'}</small>
                </td>
                <td>{group.judulTA || '-'}</td>
                <td className="text-center">
                  <Badge bg={group.proposal ? 'success' : 'secondary'}>
                    {group.proposal ? 'Selesai' : 'Belum'}
                  </Badge>
                </td>
                <td className="text-center">
                  <Badge bg={group.hasil ? 'success' : 'secondary'}>
                    {group.hasil ? 'Selesai' : 'Belum'}
                  </Badge>
                </td>
                <td className="text-center">
                  <Badge bg={group.kompre ? 'success' : 'secondary'}>
                    {group.kompre ? 'Selesai' : 'Belum'}
                  </Badge>
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleShowRekap(group)}
                    title="Rekap & Berita Acara Nilai Akhir"
                  >
                    <FaFileAlt className="me-1" /> Rekap Nilai
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  };

  const renderDetailModal = () => {
    if (!selectedSeminar) return null;

    // Check if isMahasiswaUji is true
    if (isMahasiswaUji) {
      return (
        <SeminarDetail
          seminar={selectedSeminar}
          show={showDetailModal}
          onHide={() => setShowDetailModal(false)}
          key={`detail-${selectedSeminar.id}`} // Ensure re-render with new data
        />
      );
    }

    // Default detail modal for other routes
    return (
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Detail {selectedSeminar.jenis === 'proposal' ? 'Seminar Proposal' :
              selectedSeminar.jenis === 'hasil' ? 'Seminar Hasil' : 'Sidang Komprehensif'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <p><strong>Tanggal:</strong> {new Date(selectedSeminar.tanggal).toLocaleDateString()}</p>
              <p><strong>Waktu:</strong> {selectedSeminar.waktu}</p>
              <p><strong>Tempat:</strong> {selectedSeminar.tempat}</p>
              <p>
                <strong>Status:</strong>{' '}
                <Badge bg={
                  selectedSeminar.status === 'scheduled' ? 'info' :
                    selectedSeminar.status === 'completed' ? 'success' :
                      selectedSeminar.status === 'cancelled' ? 'danger' : 'secondary'
                }>
                  {selectedSeminar.status === 'scheduled' ? 'Terjadwal' :
                    selectedSeminar.status === 'completed' ? 'Selesai' :
                      selectedSeminar.status === 'cancelled' ? 'Dibatalkan' : selectedSeminar.status}
                </Badge>
              </p>
            </Col>
            <Col md={6}>
              {(userRole === 'dosen' || userRole === 'pj') && (
                <>
                  <p><strong>Mahasiswa:</strong> {selectedSeminar.mahasiswa?.nama || selectedSeminar.mahasiswaNama || '-'}</p>
                  <p><strong>NIM:</strong> {selectedSeminar.mahasiswa?.nim || selectedSeminar.mahasiswaNIM || '-'}</p>
                </>
              )}
              <p><strong>Judul TA:</strong> {selectedSeminar.tugasAkhir?.judul || selectedSeminar.judulTA || '-'}</p>
            </Col>
          </Row>

          <hr />

          <h6>Dosen Penguji:</h6>
          <ul>
            {selectedSeminar.pengujiNama?.map((nama, index) => (
              <li key={index}>{nama}</li>
            ))}
          </ul>

          {selectedSeminar.kriteriaPenilaian && selectedSeminar.kriteriaPenilaian.length > 0 && (
            <div className="mt-4">
              <h6>Kriteria Penilaian:</h6>
              <Table responsive striped bordered>
                <thead>
                  <tr>
                    <th>Kriteria</th>
                    <th>Bobot</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSeminar.kriteriaPenilaian.map((kriteria, index) => (
                    <tr key={index}>
                      <td>{kriteria.nama}</td>
                      <td>{kriteria.bobot}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Unified scoring table is now used for all roles */}

          {(userRole === 'mahasiswa' || userRole === 'pj' || userRole === 'admin' || (userRole === 'dosen' && isPembimbing1(currentUser.uid, selectedSeminar))) &&
            selectedSeminar.nilai && Object.keys(selectedSeminar.nilai).length > 0 && (
              <div className="mt-4">
                <h6>Hasil Penilaian:</h6>
                {(userRole === 'pj' || userRole === 'admin' || (userRole === 'dosen' && isPembimbing1(currentUser.uid, selectedSeminar))) ? (
                  <>
                    <Table responsive striped bordered>
                      <thead>
                        <tr>
                          <th>Penguji</th>
                          <th>Nilai</th>
                          <th>Bobot</th>
                          <th>Kontribusi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(selectedSeminar.nilai).map((pengujiId, index) => {
                          const pengujiNilai = selectedSeminar.nilai[pengujiId];
                          const pengujiIndex = typeof selectedSeminar.pengujiId !== 'undefined' &&
                            Array.isArray(selectedSeminar.pengujiId) ?
                            selectedSeminar.pengujiId.indexOf(pengujiId) : -1;
                          const pengujiNama = pengujiIndex >= 0 && Array.isArray(selectedSeminar.pengujiNama) ?
                            selectedSeminar.pengujiNama[pengujiIndex] :
                            `Penguji ${index + 1}`;

                          // We'll use the standardized weights and roles here
                          // P1: 30%, P2: 20%, Penguji1: 30%, Penguji2: 20%

                          // Calculate score for this penguji
                          let total = 0;
                          let count = 0;

                          if (Array.isArray(selectedSeminar.kriteriaPenilaian)) {
                            selectedSeminar.kriteriaPenilaian.forEach(kriteria => {
                              if (pengujiNilai && kriteria && kriteria.id && pengujiNilai[kriteria.id]) {
                                total += pengujiNilai[kriteria.id] * (kriteria.bobot / 100);
                                count++;
                              }
                            });
                          }

                          // Determine role for display
                          const isPembimbing1 = selectedSeminar.tugasAkhir &&
                            typeof selectedSeminar.tugasAkhir === 'object' &&
                            Array.isArray(selectedSeminar.tugasAkhir.pembimbingId) &&
                            selectedSeminar.tugasAkhir.pembimbingId.length > 0 &&
                            pengujiId === selectedSeminar.tugasAkhir.pembimbingId[0];

                          const isPembimbing2 = selectedSeminar.tugasAkhir &&
                            typeof selectedSeminar.tugasAkhir === 'object' &&
                            Array.isArray(selectedSeminar.tugasAkhir.pembimbingId) &&
                            selectedSeminar.tugasAkhir.pembimbingId.length > 1 &&
                            pengujiId === selectedSeminar.tugasAkhir.pembimbingId[1];

                          // For display in the table, we'll show the role and correct weight
                          let weight = isPembimbing1 ? 0.35 :
                            isPembimbing2 ? 0.25 :
                              index === 0 || index === 1 ? 0.20 : 0.20;

                          const average = count > 0 ? total : 0;
                          const contribution = average * weight;

                          // Add role label to the name for clarity
                          const roleLabel = isPembimbing1 ? ' (Pembimbing 1)' :
                            isPembimbing2 ? ' (Pembimbing 2)' :
                              weight === 0.20 && index === 2 ? ' (Penguji 1)' : ' (Penguji 2)';

                          return (
                            <tr key={index}>
                              <td>{pengujiNama}{roleLabel}</td>
                              <td>{average.toFixed(2)}</td>
                              <td>{(weight * 100).toFixed(0)}%</td>
                              <td>{contribution.toFixed(2)}</td>
                            </tr>
                          );
                        })}

                        <tr className="table-primary">
                          <td colSpan="3"><strong>Nilai Akhir</strong></td>
                          <td>
                            <strong>
                              {calculateFinalScore(selectedSeminar).toFixed(2)}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* Tampilkan catatan masukan dari semua penguji/pembimbing */}
                    {selectedSeminar.nilai && Object.keys(selectedSeminar.nilai).length > 0 && (
                      <div className="mt-4">
                        <h6>Catatan Masukan dari Penguji/Pembimbing:</h6>
                        {Object.keys(selectedSeminar.nilai).map((pengujiId) => {
                          const pengujiNilai = selectedSeminar.nilai[pengujiId];
                          const pengujiIndex = selectedSeminar.pengujiId?.indexOf(pengujiId) ?? -1;
                          const pengujiNama = pengujiIndex >= 0 && Array.isArray(selectedSeminar.pengujiNama)
                            ? selectedSeminar.pengujiNama[pengujiIndex]
                            : 'Penguji';

                          // Determine role
                          const isP1 = selectedSeminar.tugasAkhir?.pembimbingId?.[0] === pengujiId;
                          const isP2 = selectedSeminar.tugasAkhir?.pembimbingId?.[1] === pengujiId;
                          const roleLabel = isP1 ? ' (Pembimbing 1)' : isP2 ? ' (Pembimbing 2)' : '';

                          const catatan = pengujiNilai?.catatanMasukan;
                          if (!catatan || !Array.isArray(catatan) || catatan.length === 0) {
                            return null;
                          }

                          return (
                            <div key={pengujiId} className="mb-3">
                              <strong>{pengujiNama}{roleLabel}:</strong>
                              <ul className="mt-2 mb-0">
                                {catatan.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <Alert variant="info">
                    <strong>Status Penilaian:</strong>
                    <ul className="mb-0">
                      {selectedSeminar.pengujiId && Array.isArray(selectedSeminar.pengujiId) && selectedSeminar.pengujiId.map((pengujiId, index) => {
                        const pengujiIndex = typeof selectedSeminar.pengujiId !== 'undefined' &&
                          Array.isArray(selectedSeminar.pengujiId) ?
                          selectedSeminar.pengujiId.indexOf(pengujiId) : -1;
                        const pengujiNama = pengujiIndex >= 0 && Array.isArray(selectedSeminar.pengujiNama) ?
                          selectedSeminar.pengujiNama[pengujiIndex] :
                          `Penguji ${index + 1}`;

                        // Check if this penguji has given scores
                        const hasGivenScore = selectedSeminar.nilai &&
                          typeof selectedSeminar.nilai === 'object' &&
                          selectedSeminar.nilai[pengujiId];

                        return (
                          <li key={index}>{pengujiNama}: {hasGivenScore ?
                            <Badge bg="success">Sudah Memberi Nilai</Badge> :
                            <Badge bg="warning">Belum Memberi Nilai</Badge>}
                          </li>
                        );
                      })}
                    </ul>

                    {Object.keys(selectedSeminar.nilai || {}).length === 4 ? (
                      <div className="mt-3">
                        <strong>Nilai Akhir: {calculateFinalScore(selectedSeminar).toFixed(2)}</strong>
                        <div className="mb-0 mt-2">
                          Anda dinyatakan <Badge bg={calculateFinalScore(selectedSeminar) >= 60 ? "success" : "danger"}>
                            {calculateFinalScore(selectedSeminar) >= 60 ? "LULUS" : "TIDAK LULUS"}
                          </Badge> dalam seminar ini.

                          {calculateFinalScore(selectedSeminar) < 60 && userRole === 'mahasiswa' && (
                            <div className="mt-3">
                              <Button
                                variant="warning"
                                size="sm"
                                onClick={() => {
                                  if (window.confirm(`Apakah Anda ingin mengajukan seminar ${selectedSeminar.jenis} ulang karena tidak lulus?`)) {
                                    // Need to request pembimbing 1 approval first
                                    const handleRequestReschedule = async () => {
                                      try {
                                        // Check if there's already a pending request for this seminar type
                                        const existingRequestQuery = query(
                                          collection(db, "usulanSeminar"),
                                          where("mahasiswaId", "==", currentUser.uid),
                                          where("jenis", "==", selectedSeminar.jenis),
                                          where("status", "in", ["pending_p1_approval", "pending_admin_approval"])
                                        );

                                        const existingRequestSnapshot = await getDocs(existingRequestQuery);
                                        if (!existingRequestSnapshot.empty) {
                                          setError(`Anda sudah memiliki satu permintaan penjadwalan ulang seminar ${selectedSeminar.jenis} yang sedang dalam proses. Silakan tunggu sampai proses tersebut selesai.`);
                                          return;
                                        }

                                        // Get TA data to identify pembimbing 1
                                        const taDoc = await getDoc(doc(db, "tugasAkhir", selectedSeminar.tugasAkhirId));

                                        if (!taDoc.exists()) {
                                          setError("Data tugas akhir tidak ditemukan");
                                          return;
                                        }

                                        const taData = taDoc.data();

                                        // Ensure we have pembimbing data
                                        if (!taData.pembimbingId ||
                                          (Array.isArray(taData.pembimbingId) && taData.pembimbingId.length === 0)) {
                                          setError("Data pembimbing tidak ditemukan");
                                          return;
                                        }

                                        // Get pembimbing 1 (first in the array)
                                        const pembimbing1Id = Array.isArray(taData.pembimbingId)
                                          ? taData.pembimbingId[0]
                                          : taData.pembimbingId;

                                        // Fetch pembimbing name
                                        let pembimbingName = "Dosen Pembimbing 1";
                                        const dosenDoc = await getDoc(doc(db, "users", pembimbing1Id));
                                        if (dosenDoc.exists()) {
                                          pembimbingName = dosenDoc.data().nama || "Dosen Pembimbing 1";
                                        }

                                        // Get user data for NIM
                                        let mahasiswaNIM = '-';
                                        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
                                        if (userDoc.exists()) {
                                          mahasiswaNIM = userDoc.data().nim || '-';
                                        }

                                        // Data usulan that requires P1 approval first
                                        const usulanData = {
                                          jenis: selectedSeminar.jenis,
                                          tugasAkhirId: selectedSeminar.tugasAkhirId,
                                          mahasiswaId: currentUser.uid,
                                          mahasiswaNama: currentUser.displayName || 'Mahasiswa',
                                          mahasiswaNIM: mahasiswaNIM,
                                          judulTA: selectedSeminar.judulTA || selectedSeminar.tugasAkhir?.judul || 'Tugas Akhir',
                                          pembimbing1Id: pembimbing1Id,
                                          pembimbing1Nama: pembimbingName,
                                          diajukanOleh: {
                                            mahasiswa: currentUser.uid,
                                            nama: currentUser.displayName || 'Mahasiswa',
                                            reason: "Penjadwalan ulang - tidak lulus seminar sebelumnya",
                                            timestamp: new Date().toISOString()
                                          },
                                          isReschedule: true,
                                          previousSeminarId: selectedSeminar.id,
                                          status: 'pending_p1_approval', // menunggu persetujuan pembimbing 1 dulu
                                          statusMessage: `Menunggu persetujuan ${pembimbingName}`,
                                          createdAt: new Date().toISOString()
                                        };

                                        // Simpan ke Firestore (koleksi: usulanSeminar)
                                        await addDoc(collection(db, "usulanSeminar"), usulanData);

                                        setSuccess(`Usulan penjadwalan ulang seminar ${selectedSeminar.jenis} berhasil diajukan. Silakan tunggu persetujuan dari Pembimbing 1 (${pembimbingName}) terlebih dahulu sebelum diproses oleh admin.`);
                                        setShowDetailModal(false);
                                      } catch (error) {
                                        console.error("Error submitting rescheduling request:", error);
                                        setError("Terjadi kesalahan saat mengajukan penjadwalan ulang seminar");
                                      }
                                    };

                                    handleRequestReschedule();
                                  }
                                }}
                              >
                                Ajukan Seminar Ulang
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Tampilkan catatan masukan dari semua penguji/pembimbing untuk mahasiswa */}
                        {selectedSeminar.nilai && Object.keys(selectedSeminar.nilai).length > 0 && (
                          <div className="mt-4">
                            <h6>Catatan Masukan dari Penguji/Pembimbing:</h6>
                            {Object.keys(selectedSeminar.nilai).map((pengujiId) => {
                              const pengujiNilai = selectedSeminar.nilai[pengujiId];
                              const pengujiIndex = selectedSeminar.pengujiId?.indexOf(pengujiId) ?? -1;
                              const pengujiNama = pengujiIndex >= 0 && Array.isArray(selectedSeminar.pengujiNama)
                                ? selectedSeminar.pengujiNama[pengujiIndex]
                                : 'Penguji';

                              // Determine role
                              const isP1 = selectedSeminar.tugasAkhir?.pembimbingId?.[0] === pengujiId;
                              const isP2 = selectedSeminar.tugasAkhir?.pembimbingId?.[1] === pengujiId;
                              const roleLabel = isP1 ? ' (Pembimbing 1)' : isP2 ? ' (Pembimbing 2)' : '';

                              const catatan = pengujiNilai?.catatanMasukan;
                              if (!catatan || !Array.isArray(catatan) || catatan.length === 0) {
                                return null;
                              }

                              return (
                                <div key={pengujiId} className="mb-3">
                                  <strong>{pengujiNama}{roleLabel}:</strong>
                                  <ul className="mt-2 mb-0">
                                    {catatan.map((item, idx) => (
                                      <li key={idx}>{item}</li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-3">
                        <p className="mb-0">
                          <em>Nilai akhir akan ditampilkan setelah semua penguji memberikan penilaian.</em>
                        </p>
                      </div>
                    )}
                  </Alert>
                )}

                <Alert variant="info">
                  <strong>Keterangan Pembobotan:</strong>
                  <ul className="mb-0">
                    <li>Pembimbing 1: 35%</li>
                    <li>Pembimbing 2: 25%</li>
                    <li>Penguji 1: 20%</li>
                    <li>Penguji 2: 20%</li>
                  </ul>
                  {Object.keys(selectedSeminar.nilai || {}).length < 4 && (
                    <div className="mt-2">
                      <strong>Catatan:</strong> Nilai akhir akan dihitung setelah seluruh penguji memberikan penilaian.
                    </div>
                  )}
                </Alert>
              </div>
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal >
    );
  };

  const renderNilaiModal = () => {
    if (!selectedSeminar) return null;

    // Cek apakah user sudah pernah mengisi nilai sebelumnya
    const hasExistingNilai = selectedSeminar.nilai && selectedSeminar.nilai[currentUser.uid];
    const isCompleted = selectedSeminar.status === 'completed';

    return (
      <Modal show={showNilaiModal} onHide={() => setShowNilaiModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {hasExistingNilai ? 'Edit Penilaian' : 'Penilaian'} {selectedSeminar.jenis === 'proposal' ? 'Seminar Proposal' :
              selectedSeminar.jenis === 'hasil' ? 'Seminar Hasil' : 'Sidang Komprehensif'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          {/* Tampilkan alert jika sedang edit nilai yang sudah completed */}
          {hasExistingNilai && isCompleted && (
            <Alert variant="info">
              <strong>Catatan:</strong> Anda sedang mengubah nilai yang sudah pernah diisi.
              Nilai yang diubah akan langsung memperbarui nilai akhir seminar.
            </Alert>
          )}

          <div className="mb-4">
            <h6>Informasi Seminar:</h6>
            <p><strong>Mahasiswa:</strong> {selectedSeminar.mahasiswa?.nama || selectedSeminar.mahasiswaNama || '-'}</p>
            <p><strong>Judul TA:</strong> {selectedSeminar.tugasAkhir?.judul || selectedSeminar.judulTA || '-'}</p>
            <p><strong>Tanggal:</strong> {new Date(selectedSeminar.tanggal).toLocaleDateString()}</p>
            <p>
              <strong>Status:</strong>{' '}
              <Badge bg={isCompleted ? 'success' : 'info'}>
                {isCompleted ? 'Selesai' : 'Terjadwal'}
              </Badge>
            </p>
          </div>

          <hr />

          <h6>Form Penilaian:</h6>
          <Form>
            {selectedSeminar.kriteriaPenilaian?.map((kriteria, index) => (
              <Form.Group key={index} className="mb-3">
                <Form.Label>
                  {kriteria.nama} (Bobot: {kriteria.bobot}%)
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={nilaiForm[kriteria.id] || 0}
                  onChange={(e) => handleNilaiChange(kriteria.id, e.target.value)}
                />
                <Form.Text className="text-muted">
                  Nilai 0-100
                </Form.Text>
              </Form.Group>
            ))}
          </Form>

          <hr className="my-4" />

          <h6>Catatan Masukan:</h6>
          <Form.Text className="text-muted d-block mb-3">
            Masukkan poin-poin catatan atau masukan untuk mahasiswa (opsional)
          </Form.Text>

          {catatanMasukan.length === 0 && (
            <div className="mb-2 text-muted">Belum ada catatan masukan.</div>
          )}
          {catatanMasukan.map((catatan, idx) => (
            <div key={idx} className="d-flex mb-2 gap-2">
              <Form.Control
                type="text"
                value={catatan}
                onChange={(e) => handleCatatanChange(idx, e.target.value)}
                placeholder={`Catatan masukan #${idx + 1}`}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemoveCatatan(idx)}
                disabled={loading}
              >
                &times;
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAddCatatan}
            disabled={loading}
            className="mb-3"
          >
            + Tambah Catatan Masukan
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNilaiModal(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitNilai}
            disabled={loading}
          >
            {loading ? 'Menyimpan...' : 'Simpan Nilai'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  if (loading && !seminarList.length) {
    return (
      <Container className="py-4">
        <Alert variant="info">Memuat data...</Alert>
      </Container>
    );
  }

  // Jika mahasiswa belum mendaftar tugas akhir atau belum disetujui
  if (userRole === 'mahasiswa' && (!tugasAkhir || tugasAkhir.status !== 'approved')) {
    return (
      <Container className="py-4">
        <Card>
          <Card.Body>
            <Card.Title>Seminar & Sidang</Card.Title>
            <Alert variant="warning">
              {!tugasAkhir
                ? "Anda belum mendaftar tugas akhir. Silakan daftar terlebih dahulu."
                : "Tugas akhir Anda belum disetujui. Silakan tunggu persetujuan dari Penanggung Jawab Tugas Akhir."}
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Body>
          <Card.Title>Seminar & Sidang</Card.Title>

          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
          >
            <Tab eventKey="proposal" title="Seminar Proposal">
              {renderSeminarTable('proposal')}
            </Tab>
            <Tab eventKey="hasil" title="Seminar Hasil">
              {renderSeminarTable('hasil')}
            </Tab>
            <Tab eventKey="kompre" title="Sidang Komprehensif">
              {renderSeminarTable('kompre')}
            </Tab>
            {(userRole === 'dosen' || userRole === 'pj') && (
              <Tab eventKey="nilaiAkhir" title="Nilai Akhir">
                {renderNilaiAkhirTable()}
              </Tab>
            )}
          </Tabs>

          {renderDetailModal()}
          {renderNilaiModal()}

          {showRekapModal && selectedGroup && (
            <RekapNilaiAkhir
              group={selectedGroup}
              show={showRekapModal}
              onHide={() => setShowRekapModal(false)}
            />
          )}
        </Card.Body>
      </Card>

      {/* Modal untuk memilih penerima undangan */}
      <Modal show={showUndanganModal} onHide={() => setShowUndanganModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Generate Undangan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSeminar && (
            <>
              <Alert variant="info">
                <p>Pilih pembimbing atau penguji untuk generate undangan:</p>
              </Alert>

              {/* List pembimbing */}
              {(() => {
                // Use tugasAkhirWithPembimbing if available, otherwise fallback to tugasAkhir
                const taData = tugasAkhirWithPembimbing || tugasAkhir;
                const pembimbingList = taData?.pembimbing || [];
                const pembimbingIdList = taData?.pembimbingId || [];

                if (pembimbingList.length > 0 || (Array.isArray(pembimbingIdList) && pembimbingIdList.length > 0)) {
                  return (
                    <div className="mb-3">
                      <h6>Pembimbing:</h6>
                      {(pembimbingList.length > 0 ? pembimbingList : pembimbingIdList).map((p, idx) => {
                        const pembimbingId = typeof p === 'string' ? p : (Array.isArray(pembimbingIdList) ? pembimbingIdList[idx] : null);
                        const pembimbingData = typeof p === 'object' ? p : null;

                        if (!pembimbingId) return null;

                        return (
                          <Button
                            key={pembimbingId}
                            variant="outline-primary"
                            className="d-block w-100 mb-2"
                            onClick={() => {
                              handleGenerateUndangan(selectedSeminar, pembimbingId, `Pembimbing ${idx + 1}`);
                              setShowUndanganModal(false);
                            }}
                          >
                            Generate Undangan untuk Pembimbing {idx + 1}
                            <br />
                            <small>{pembimbingData?.nama || 'Pembimbing'} {pembimbingData?.nip ? `(NIP: ${pembimbingData.nip})` : ''}</small>
                          </Button>
                        );
                      })}
                    </div>
                  );
                }
                return null;
              })()}

              {/* List penguji */}
              {selectedSeminar.pengujiId && Array.isArray(selectedSeminar.pengujiId) && selectedSeminar.pengujiId.length > 0 && (
                <div className="mb-3">
                  <h6>Penguji:</h6>
                  {(() => {
                    const taData = tugasAkhirWithPembimbing || tugasAkhir || selectedSeminar.tugasAkhir;
                    const pembimbing1Id = taData?.pembimbingId?.[0];
                    const pembimbing2Id = taData?.pembimbingId?.[1];
                    let pengujiCounter = 1;

                    return selectedSeminar.pengujiId.map((pengujiId, idx) => {
                      // Skip jika ini adalah pembimbing
                      if (pengujiId === pembimbing1Id || pengujiId === pembimbing2Id) {
                        return null;
                      }

                      const pengujiNama = selectedSeminar.pengujiNama?.[idx] || `Penguji ${pengujiCounter}`;
                      const currentPengujiNum = pengujiCounter;
                      pengujiCounter++;

                      return (
                        <Button
                          key={pengujiId}
                          variant="outline-success"
                          className="d-block w-100 mb-2"
                          onClick={() => {
                            handleGenerateUndangan(selectedSeminar, pengujiId, `Penguji ${currentPengujiNum}`);
                            setShowUndanganModal(false);
                          }}
                        >
                          Generate Undangan untuk Penguji {currentPengujiNum}
                          <br />
                          <small>{pengujiNama}</small>
                        </Button>
                      );
                    });
                  })()}
                </div>
              )}

              {(!tugasAkhir?.pembimbing || tugasAkhir.pembimbing.length === 0) &&
                (!selectedSeminar.pengujiId || selectedSeminar.pengujiId.length === 0) && (
                  <Alert variant="warning">
                    Data pembimbing atau penguji tidak tersedia.
                  </Alert>
                )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUndanganModal(false)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Seminar;
